import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartCard } from "./chart-card";
import { Button } from "@/components/ui/button";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Legend,
	PieChart,
	Pie,
	Cell,
} from "recharts";
import { MapPin, Users, Globe, Download } from "lucide-react";
import { useAuth } from "@/global-hooks/useAuth";
import { downloadChartPng } from "./utils";
import { toAPIFilter } from "./utils";
import {
	BAR_VALUE_LABEL,
	NO_ANIMATION,
	CATEGORY_AXIS_EXTRA_HEIGHT,
	CATEGORY_AXIS_HEIGHT,
	WrappedCategoryTick,
	chartMinWidth,
} from "./chart-ticks";
import type { StatisticsFilters } from "./StatisticsControls";
import statisticsAPIService, { type StatisticsByProvince } from "@/lib/statistics-api.service";

interface ProvinceDemographicsTabProps {
	/** Bộ lọc của cả trang (khoa, khoá, khoảng ngày). Thiếu nó thì mục này
	 * hiện số của mọi khoá ngay cạnh các tab đã lọc. */
	filters?: StatisticsFilters;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

const COLORS = [
	"#0088FE",
	"#00C49F",
	"#FFBB28",
	"#FF8042",
	"#8884D8",
	"#82CA9D",
	"#FFC658",
	"#8DD1E1",
];

export default function ProvinceDemographicsTab({
	filters,
	refreshToken = 0,
}: ProvinceDemographicsTabProps) {
	const provinceRef = useRef<HTMLDivElement>(null);
	const { user } = useAuth();
	const [provinceStats, setProvinceStats] = useState<StatisticsByProvince[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const canExport = user?.role === "admin" || user?.role === "manager";

	useEffect(() => {
		const fetchProvinceStats = async () => {
			try {
				setIsLoading(true);
				const data = await statisticsAPIService.getProvinceStatistics(
					toAPIFilter(filters, "total"),
				);
				setProvinceStats(data);
			} catch (err) {
				console.error("Error fetching province demographics:", err);
				setError(err instanceof Error ? err.message : "Failed to load province demographics");
			} finally {
				setIsLoading(false);
			}
		};

		fetchProvinceStats();
		// `refreshToken`: nút Tải lại của trang phải làm mới cả mục này.
	}, [refreshToken, filters]);

	const totalStudents = provinceStats.reduce((sum, item) => sum + item.total, 0);
	const topProvince = provinceStats.length > 0 ? provinceStats[0] : null;
	const totalProvinces = provinceStats.length;

	// Get top 10 provinces for detailed view
	const top10Provinces = provinceStats.slice(0, 10);

	const barData = top10Provinces.map((item) => ({
		// Full name, never truncated: the X axis labels wrap now, so narrow space is
		// no longer a reason to replace the end of a name with an ellipsis.
		name: item.provinceName,
		fullName: item.provinceName,
		"Tổng số": item.total,
		Nam: item.male,
		Nữ: item.female,
		percentage: item.percentage,
	}));

	// Get top 8 provinces for pie chart
	const pieData = provinceStats.slice(0, 8).map((item) => ({
		name: item.provinceName,
		value: item.total,
		percentage: item.percentage,
	}));

	// Chỉ chiếm chỗ khi chưa có gì để hiện. Lần tải lại sau đó giữ nguyên biểu đồ
	// cũ thay vì thay nó bằng một dòng chữ rồi dựng lại từ đầu.
	if (isLoading && provinceStats.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="text-center">
						<div className="animate-pulse text-lg">Đang tải thống kê địa lý...</div>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="text-center text-red-600 dark:text-red-400">
						<div className="text-lg font-medium">Lỗi tải dữ liệu</div>
						<div className="text-sm">{error}</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div ref={provinceRef}>
				<div className="flex items-center justify-between mb-6">
					<div className="flex items-center gap-3">
						<Globe className="h-6 w-6 text-green-700 dark:text-green-400" />
						<div>
							<h2 className="text-2xl font-bold">Phân bố theo Tỉnh thành</h2>
							<p className="text-muted-foreground">Thống kê địa lý sinh viên</p>
						</div>
					</div>

					<div className="flex items-center gap-2">
						{canExport && (
							<Button
								onClick={() => {
									console.log("Export province demographics as CSV");
								}}
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
							>
								<Download className="h-4 w-4" />
								Tải CSV
							</Button>
						)}
						<Button
							onClick={() => downloadChartPng(provinceRef, "phan-bo-theo-tinh-thanh")}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<Download className="h-4 w-4" />
							Tải PNG
						</Button>
					</div>
				</div>

				{/* Summary Cards */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Tổng sinh viên</CardTitle>
							<Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
								{totalStudents.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">Có thông tin địa lý</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Số tỉnh/thành</CardTitle>
							<MapPin className="h-4 w-4 text-green-700 dark:text-green-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-green-700 dark:text-green-400">
								{totalProvinces}
							</div>
							<p className="text-xs text-muted-foreground">Tỉnh thành có sinh viên</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Tỉnh đông nhất</CardTitle>
							<Globe className="h-4 w-4 text-purple-600 dark:text-purple-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
								{topProvince ? topProvince.provinceName : "N/A"}
							</div>
							<p className="text-xs text-muted-foreground">
								{topProvince ? `${topProvince.total.toLocaleString()} sinh viên` : ""}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Tỷ lệ tỉnh đông nhất</CardTitle>
							<MapPin className="h-4 w-4 text-orange-700 dark:text-orange-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-orange-700 dark:text-orange-400">
								{topProvince ? `${topProvince.percentage.toFixed(1)}%` : "N/A"}
							</div>
							<p className="text-xs text-muted-foreground">Tỷ trọng cao nhất</p>
						</CardContent>
					</Card>
				</div>

				{/* Charts */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
					{/* Bar Chart - Top 10 */}
					<ChartCard
						title="Top 10 tỉnh thành"
						filename="top-10-tinh-thanh"
						icon={<BarChart className="h-5 w-5" />}
					>
						<div className="overflow-x-auto" style={{ height: 320 + CATEGORY_AXIS_EXTRA_HEIGHT }}>
							<div style={{ minWidth: chartMinWidth(barData.length), height: "100%" }}>
								<ResponsiveContainer width="100%" height="100%">
									<BarChart data={barData}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis
											dataKey="name"
											interval={0}
											tick={<WrappedCategoryTick />}
											height={CATEGORY_AXIS_HEIGHT}
										/>
										<YAxis />
										<Tooltip
											formatter={(value: number, name: string) => [value.toLocaleString(), name]}
											labelFormatter={(label) => {
												const item = barData.find((d) => d.name === label);
												return item ? item.fullName : label;
											}}
										/>
										<Legend />
										<Bar
											{...NO_ANIMATION}
											dataKey="Tổng số"
											fill="#3b82f6"
											radius={[4, 4, 0, 0]}
											label={BAR_VALUE_LABEL}
										/>
										<Bar
											{...NO_ANIMATION}
											dataKey="Nam"
											fill="#10b981"
											radius={[4, 4, 0, 0]}
											label={BAR_VALUE_LABEL}
										/>
										<Bar
											{...NO_ANIMATION}
											dataKey="Nữ"
											fill="#f59e0b"
											radius={[4, 4, 0, 0]}
											label={BAR_VALUE_LABEL}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>
						</div>
					</ChartCard>

					{/* Pie Chart - Top 8 */}
					<ChartCard
						title="Tỷ lệ phân bố (Top 8)"
						filename="ty-le-phan-bo-tinh-thanh"
						icon={<PieChart className="h-5 w-5" />}
					>
						<div className="h-80">
							<ResponsiveContainer width="100%" height="100%">
								<PieChart>
									<Pie
										{...NO_ANIMATION}
										data={pieData}
										cx="50%"
										cy="50%"
										labelLine={false}
										label={({ percentage }) => `${percentage.toFixed(1)}%`}
										outerRadius={80}
										fill="#8884d8"
										dataKey="value"
									>
										{pieData.map((_, index) => (
											<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
										))}
									</Pie>
									<Tooltip
										formatter={(value: number, _name: string) => [
											value.toLocaleString(),
											"Sinh viên",
										]}
										labelFormatter={(label: string, payload: any[]) => {
											if (payload && payload.length > 0) {
												return payload[0].payload.name;
											}
											return label;
										}}
									/>
									<Legend />
								</PieChart>
							</ResponsiveContainer>
						</div>
					</ChartCard>
				</div>

				{/* Detailed Table */}
				<Card>
					<CardHeader>
						<CardTitle>Chi tiết phân bố theo tỉnh thành</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<table className="w-full border-collapse">
								<thead>
									<tr className="border-b">
										<th className="text-left p-3 font-medium">Tỉnh/Thành</th>
										<th className="text-right p-3 font-medium">Tổng số</th>
										<th className="text-right p-3 font-medium">Nam</th>
										<th className="text-right p-3 font-medium">Nữ</th>
										<th className="text-right p-3 font-medium">Tỷ lệ</th>
									</tr>
								</thead>
								<tbody>
									{provinceStats.map((item, index) => (
										<tr key={item.provinceCode} className="border-b hover:bg-accent">
											<td className="p-3">
												<div className="flex items-center gap-2">
													<span className="flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 rounded-full text-xs font-bold">
														{index + 1}
													</span>
													<span className="font-medium">{item.provinceName}</span>
												</div>
											</td>
											<td className="p-3 text-right font-bold">{item.total.toLocaleString()}</td>
											<td className="p-3 text-right text-blue-600 dark:text-blue-400">
												{item.male.toLocaleString()}
											</td>
											<td className="p-3 text-right text-pink-600 dark:text-pink-400">
												{item.female.toLocaleString()}
											</td>
											<td className="p-3 text-right font-medium">{item.percentage.toFixed(2)}%</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
